/**
 * Independent copy of the existing sort sheet for PLP V3/V4.
 * Keeps the same options, draft selection and parent callbacks so presentation can evolve separately.
 */
import { useState } from 'react';
import { X } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle, SheetClose } from '@/shared/widgets/sheet';
import { PLP_SORT_OPTIONS, type PlpSortByValue } from '../../constants';

interface PlpSortSheetV2Props {
  open: boolean;
  currentSortBy: PlpSortByValue;
  onOpenChange: (open: boolean) => void;
  onApply: (nextSortBy: PlpSortByValue) => void;
  /** Analytics: fires when user taps a sort radio (draft selection, not yet applied) */
  onSortOptionSelect?: (value: PlpSortByValue) => void;
  /** Analytics: fires when user taps the Cancel button */
  onCancel?: () => void;
}

export function PlpSortSheetV2({
  open,
  currentSortBy,
  onOpenChange,
  onApply,
  onSortOptionSelect,
  onCancel,
}: PlpSortSheetV2Props) {
  const [draftSortBy, setDraftSortBy] = useState<PlpSortByValue>(currentSortBy);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) setDraftSortBy(currentSortBy);
    onOpenChange(nextOpen);
  };

  const handleApply = () => {
    onApply(draftSortBy);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="bottom"
        className="animate-bottom-sheet inset-x-0 bottom-0 flex h-auto max-h-[85dvh] flex-col gap-0 overflow-hidden rounded-t-[32px] rounded-b-none bg-white p-0"
        aria-describedby={undefined}
        showCloseButton={false}
      >
        {/* Fit the sheet to its content; on short screens only the options scroll, keeping the actions visible. */}
        <div className="flex min-h-0 flex-col bg-white">
          <div className="flex shrink-0 items-center justify-between gap-4 border-b border-gray-100 px-4 pb-3 pt-8">
            <SheetTitle className="font-serif text-2xl font-bold text-[#15366F]">Sort By</SheetTitle>
            <SheetClose className="p-1 text-black" aria-label="Close sort selection">
              <X className="h-6 w-6" />
            </SheetClose>
          </div>
          <div className="min-h-0 overflow-y-auto">
            <div role="group" aria-label="Sort options">
              {PLP_SORT_OPTIONS.map((option) => {
                const selected = draftSortBy === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className="flex min-h-[54px] w-full items-center justify-between gap-3 border-b border-gray-100 px-5 py-4 text-left"
                    aria-pressed={selected}
                    onClick={() => {
                      onSortOptionSelect?.(option.value);
                      setDraftSortBy(option.value);
                    }}
                  >
                    <span className="text-base text-[#092B67]">{option.label}</span>
                    <span
                      className={[
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                        selected
                          ? 'border-[#15366F] bg-[#15366F]'
                          : 'border-gray-300 bg-white',
                      ].join(' ')}
                      aria-hidden
                    >
                      {selected && <span className="h-2 w-2 rounded-full bg-white" />}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid shrink-0 grid-cols-2 gap-2 px-6 pt-4 pb-[max(20px,env(safe-area-inset-bottom))]">
            <button
              type="button"
              className="h-11 rounded-xl border border-[#CFE0FF] bg-white text-base text-[#092B67]"
              aria-label="Cancel sort selection"
              onClick={() => { onCancel?.(); onOpenChange(false); }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="h-11 rounded-xl bg-[#15366F] text-base text-white"
              aria-label="Apply sort selection"
              onClick={handleApply}
            >
              Apply
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

